import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { links, tradingAccountDetailsRouteUrl } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'

import { TradingAccountTab } from '../Navigation/TradingAccountNavigation'
import LiveTradingAccountDetailsPage from './LiveTradingAccountDetailsPage'
import TradingAccountPendingOrdersPage from './TradingAccountPendingOrdersPage'
import TradingAccountPlatformTransactionDetailsPage from './TradingAccountPlatformTransactionDetailsPage'
import TradingAccountPositionsPage from './TradingAccountPositionsPage'
import TradingAccountStatisticsDetailsPage from './TradingAccountStatisticsDetailsPage'
import TradingAccountTransactionDetailsPage from './TradingAccountTransactionDetailsPage'

const ResolveTradingAccountRoute = (): JSX.Element => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const params = useParams<{ id: string; slug: TradingAccountTab }>()
  const { id, slug } = params

  const [auth] = useContext(AuthSessionContext)
  const [crumbs, setCrumbs] = useState([
    { title: 'Trading Accounts - Live', link: links.recordsTradingAccounts.url },
  ])

  const permissions = auth?.permissions || []

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(() => {
    return id ? tradingAccountsApiClient.getTradingAccountDetails(id) : Promise.resolve(null)
  }, [id])
  const { data: tradingAccount, isLoading } = useFetchOne(fetchData)

  useEffect(() => {
    if (tradingAccount) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: tradingAccount.name,
          link: tradingAccountDetailsRouteUrl(
            tradingAccount,
            slug
              ? matchesSlug(slug)
                ? slug
                : TradingAccountTab.Details
              : TradingAccountTab.Details
          ),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [tradingAccount]) // eslint-disable-line react-hooks/exhaustive-deps

  const matchesSlug = (tradingAccountDetailsSlug?: TradingAccountTab) => {
    if (!tradingAccountDetailsSlug) {
      return false
    }
    return (
      tradingAccountDetailsSlug === TradingAccountTab.Details ||
      tradingAccountDetailsSlug === TradingAccountTab.Statistics ||
      tradingAccountDetailsSlug === TradingAccountTab.Transactions ||
      tradingAccountDetailsSlug === TradingAccountTab.Orders ||
      tradingAccountDetailsSlug === TradingAccountTab.Positions ||
      tradingAccountDetailsSlug === TradingAccountTab.PlatformTransactions
    )
  }

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!tradingAccount) {
      return <PageNotFound />
    }
    if (
      slug === TradingAccountTab.Details &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccount)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <LiveTradingAccountDetailsPage data={tradingAccount} forceRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (
      slug === TradingAccountTab.Statistics &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccountStatistics)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <TradingAccountStatisticsDetailsPage data={tradingAccount} />
        </React.Fragment>
      )
    }
    if (
      slug === TradingAccountTab.Positions &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccountOpenPositions)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <TradingAccountPositionsPage data={tradingAccount} />
        </React.Fragment>
      )
    }
    if (
      slug === TradingAccountTab.Orders &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccountPendingOrders)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <TradingAccountPendingOrdersPage data={tradingAccount} />
        </React.Fragment>
      )
    }
    if (
      slug === TradingAccountTab.Transactions &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccountTransactions)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <TradingAccountTransactionDetailsPage data={tradingAccount} />
        </React.Fragment>
      )
    }
    if (
      slug === TradingAccountTab.PlatformTransactions &&
      hasPermission(permissions, PermissionsEnum.CanViewTradingAccountPlatformTransactions)
    ) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <TradingAccountPlatformTransactionDetailsPage data={tradingAccount} />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return <Loading isLoading={isLoading && !matchesSlug(slug)}>{innerRender()}</Loading>
}

export default ResolveTradingAccountRoute
